// src/utils/cookies.js
// Cookie consent management utility

const CONSENT_KEY = 'cookieConsent';
const CONSENT_COOKIE_NAME = 'cookie_consent';
const CONSENT_EXPIRY_DAYS = 365;

export const COOKIE_CATEGORIES = {
  ESSENTIAL: 'essential',
  FUNCTIONAL: 'functional',
  ANALYTICS: 'analytics'
};

/**
 * Get current cookie consent preferences
 * @returns {Object|null} Consent preferences or null if not set
 */
export function getCookieConsent() {
  try {
    // Check localStorage first (primary storage)
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
    
    // Fallback to cookie (for server-side checks if needed)
    const cookie = getCookie(CONSENT_COOKIE_NAME);
    if (cookie) {
      return JSON.parse(decodeURIComponent(cookie));
    }
    
    return null;
  } catch (error) {
    console.error('Error reading cookie consent:', error);
    return null;
  }
}

/**
 * Check if user has given consent for a specific category
 * @param {string} category - Cookie category (essential, functional, analytics)
 * @returns {boolean}
 */
export function hasConsent(category) {
  const consent = getCookieConsent();
  if (!consent) return false;
  
  // Essential cookies are always allowed
  if (category === COOKIE_CATEGORIES.ESSENTIAL) return true;
  
  return consent[category] === true;
}

/**
 * Check if user has given any consent
 * @returns {boolean}
 */
export function hasAnyConsent() {
  const consent = getCookieConsent();
  return consent !== null;
}

/**
 * Save cookie consent preferences
 * @param {Object} preferences - Consent preferences object
 */
export function setCookieConsent(preferences) {
  try {
    const consentData = {
      ...preferences,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    
    // Store in localStorage (primary)
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData));
    
    // Also store in cookie (for server-side checks)
    setCookie(CONSENT_COOKIE_NAME, JSON.stringify(consentData), CONSENT_EXPIRY_DAYS);
    
    // Dispatch event for other parts of the app to react
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: consentData }));
  } catch (error) {
    console.error('Error saving cookie consent:', error);
  }
}

/**
 * Set a cookie
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} days - Expiry in days
 */
function setCookie(name, value, days) {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}

/**
 * Get a cookie value
 * @param {string} name - Cookie name
 * @returns {string|null}
 */
function getCookie(name) {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length, c.length));
    }
  }
  return null;
}

/**
 * Clear all cookie consent
 */
export function clearCookieConsent() {
  localStorage.removeItem(CONSENT_KEY);
  setCookie(CONSENT_COOKIE_NAME, '', -1);
  window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: null }));
}
